if (!window.Eurus.loadedScript.has('variant-select-card.js')) {
  window.Eurus.loadedScript.add('variant-select-card.js');

  const normalizeOptionValue = (value) => String(value ?? '')
    .replace(/\\u003c/gi, '<')
    .replace(/\\u003e/gi, '>')
    .trim()
    .toLocaleLowerCase();

  const getContextualVariantContainer = (card) =>
    card?.querySelector('.x-variants-data-js [data-contextual-variants]')?.closest('.x-variants-data-js');

  const getContextualVariants = (container) => {
    const payload = container?.querySelector('[data-contextual-variants]');
    if (!payload) return [];

    try {
      const variants = JSON.parse(payload.textContent).flat(Infinity);
      const uniqueVariants = new Map();
      variants.forEach((variant) => {
        if (variant?.id && variant?.productId && Array.isArray(variant.options)) {
          uniqueVariants.set(`${variant.productId}:${variant.id}`, variant);
        }
      });
      return Array.from(uniqueVariants.values());
    } catch (error) {
      console.error('Unable to parse contextual card variants.', error);
      return [];
    }
  };

  const getSelectedContextualOptions = (container) => {
    const selectedOptions = new Map();
    container?.querySelectorAll('fieldset input:checked[data-option-name]').forEach((input) => {
      selectedOptions.set(normalizeOptionValue(input.dataset.optionName), normalizeOptionValue(input.value));
    });
    return selectedOptions;
  };

  const variantMatchesOptions = (variant, selectedOptions, skippedOptionName = '') => {
    const variantOptions = new Map(
      variant.options.map((option) => [normalizeOptionValue(option.name), normalizeOptionValue(option.value)])
    );

    for (const [optionName, optionValue] of selectedOptions) {
      if (optionName !== skippedOptionName && variantOptions.get(optionName) !== optionValue) return false;
    }
    return true;
  };

  const pickContextualVariant = (variants, preferredProductId = '') => {
    const preferredVariants = preferredProductId
      ? variants.filter((variant) => String(variant.productId) === String(preferredProductId))
      : variants;
    const candidates = preferredVariants.length > 0 ? preferredVariants : variants;
    return candidates.find((variant) => variant.available) || candidates[0] || null;
  };

  const setContextualInputState = (input, variant) => {
    const selectable = Boolean(variant);
    const available = Boolean(variant?.available);
    const label = input.id ? input.closest('fieldset')?.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;

    input.disabled = !selectable;
    input.toggleAttribute('aria-disabled', !selectable);
    input.classList.toggle('disabled', !available);
    input.dataset.available = String(available);

    if (variant) {
      input.dataset.cardVariantId = String(variant.id);
      input.dataset.cardVariantHasMedia = String(Boolean(variant.hasMedia));
      input.dataset.cardProductId = String(variant.productId);
      input.dataset.productUrl = buildVariantUrl(variant.productUrl, variant.id);
    } else {
      delete input.dataset.cardVariantId;
      delete input.dataset.cardVariantHasMedia;
      delete input.dataset.cardProductId;
      delete input.dataset.productUrl;
    }

    if (label) {
      label.classList.toggle('cursor-pointer', selectable);
      label.classList.toggle('cursor-not-allowed', !selectable);
      label.classList.toggle('pointer-events-none', !selectable);
      label.classList.toggle('opacity-50', !selectable);
      label.tabIndex = selectable ? 0 : -1;
      label.toggleAttribute('aria-disabled', !selectable);
      if (label.dataset.cardVariantImageSwatch === 'true') {
        if (variant?.mediaUrl) {
          label.style.setProperty('--bg-image', `url("${variant.mediaUrl}")`);
        } else {
          label.style.removeProperty('--bg-image');
        }
      }
    }
  };

  const buildVariantUrl = (url, variantId) => {
    const variantUrl = new URL(url, window.location.origin);
    variantUrl.searchParams.delete('variant');
    variantUrl.searchParams.set('variant', String(variantId));
    return variantUrl.origin === window.location.origin
      ? `${variantUrl.pathname}${variantUrl.search}${variantUrl.hash}`
      : variantUrl.toString();
  };

  const CONTEXTUAL_OPTION_SELECTOR = 'input[data-contextual-variant-option="true"]';
  const CONTEXTUAL_PENDING_ATTRIBUTE = 'data-contextual-variant-pending';
  const contextualTransactionStates = new WeakMap();
  const activeContextualTransactionStates = new Set();

  const getRenderedFullVariant = (card) => {
    const payload = card?.querySelector('script[type="application/json"][data-resolved-current-variant]');
    if (!payload) return null;

    try {
      const variant = JSON.parse(payload.textContent);
      return variant && typeof variant === 'object' && !Array.isArray(variant) ? variant : null;
    } catch (_error) {
      return null;
    }
  };

  const getAlpineData = (element) => {
    if (!element || !window.Alpine?.$data) return null;
    try {
      return window.Alpine.$data(element);
    } catch (_error) {
      return null;
    }
  };

  const setContextualLoadingState = (card, input) => {
    const variantContainer = getContextualVariantContainer(card);
    const variantState = getAlpineData(variantContainer);
    const cardState = getAlpineData(card);
    if (variantState && 'loadingEls' in variantState) variantState.loadingEls = input.id || '';
    if (cardState && 'loadingProductEls' in cardState) {
      cardState.loadingProductEls = input.getAttribute('section-id') || '';
    }
  };

  const clearContextualLoadingState = (card) => {
    const variantState = getAlpineData(getContextualVariantContainer(card));
    const cardState = getAlpineData(card);
    if (variantState && 'loadingEls' in variantState) variantState.loadingEls = '';
    if (cardState && 'loadingProductEls' in cardState) cardState.loadingProductEls = '';

    card?.querySelectorAll('fieldset[x-data]').forEach((fieldset) => {
      const fieldsetState = getAlpineData(fieldset);
      if (fieldsetState && 'activeSwatch' in fieldsetState) fieldsetState.activeSwatch = false;
    });
  };

  const synchronizeResolvedVariantCard = (card, preferredProductId = '', applyCardState = true, preferredVariantId = '') => {
    const container = getContextualVariantContainer(card);
    const variants = getContextualVariants(container);
    if (!container || variants.length === 0) return null;

    const selectedOptions = getSelectedContextualOptions(container);
    container.querySelectorAll('fieldset input[data-option-name]').forEach((input) => {
      const optionName = normalizeOptionValue(input.dataset.optionName);
      const candidateOptions = new Map(selectedOptions);
      candidateOptions.set(optionName, normalizeOptionValue(input.value));
      const candidates = variants.filter((variant) => variantMatchesOptions(variant, candidateOptions));
      const mappedVariant = pickContextualVariant(candidates, input.dataset.cardOriginProductId);
      setContextualInputState(input, mappedVariant);
    });

    const exactVariants = variants.filter((variant) => variantMatchesOptions(variant, selectedOptions));
    const exactVariant = exactVariants.find((variant) => String(variant.id) === String(preferredVariantId))
      || pickContextualVariant(exactVariants, preferredProductId);
    if (!exactVariant || !applyCardState) return exactVariant;

    const variantId = String(exactVariant.id);
    const productUrl = String(exactVariant.productId) === String(container.dataset.contextualProductId)
      ? container.dataset.contextualProductUrlBase
      : exactVariant.productUrl;
    const variantUrl = buildVariantUrl(productUrl, variantId);

    card.querySelectorAll('.link-product-variant').forEach((link) => {
      link.href = variantUrl;
    });
    card.querySelectorAll('.current-variant').forEach((marker) => {
      marker.textContent = variantId;
    });
    card.querySelectorAll('.contextual-quick-add-form').forEach((form) => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variantId;
      form.querySelectorAll('button[type="submit"]').forEach((button) => {
        button.disabled = !exactVariant.available;
      });
    });
    card.dataset.resolvedVariantId = variantId;

    card.querySelectorAll('.preview-img').forEach((image) => {
      image.classList.toggle('hidden', !exactVariant.hasMedia);
    });
    card.querySelectorAll('.resolved-variant-placeholder').forEach((placeholder) => {
      placeholder.classList.toggle('hidden', exactVariant.hasMedia);
      placeholder.classList.toggle('flex', !exactVariant.hasMedia);
    });

    return exactVariant;
  };

  const reconcileRenderedVariantCard = (card) => {
    const container = getContextualVariantContainer(card);
    const renderedVariantId = card?.dataset.resolvedVariantId;
    const variants = getContextualVariants(container);
    const renderedVariant = variants.find((variant) => String(variant.id) === String(renderedVariantId));
    if (!container || !renderedVariant) return null;

    const renderedOptions = new Map(
      renderedVariant.options.map((option) => [normalizeOptionValue(option.name), normalizeOptionValue(option.value)])
    );
    container.querySelectorAll('fieldset input[data-option-name]').forEach((input) => {
      const optionName = normalizeOptionValue(input.dataset.optionName);
      if (renderedOptions.has(optionName)) {
        input.checked = renderedOptions.get(optionName) === normalizeOptionValue(input.value);
      }
    });

    return synchronizeResolvedVariantCard(card, renderedVariant.productId, true, renderedVariant.id);
  };

  const resolveContextualVariantTarget = (input, card) => {
    const container = getContextualVariantContainer(card);
    if (!input || !container) return null;

    const exactVariant = synchronizeResolvedVariantCard(card, input.dataset.cardProductId, false);
    if (!exactVariant) return null;

    container.dataset.contextualTargetProductId = String(exactVariant.productId);
    return {
      productId: String(exactVariant.productId),
      url: buildVariantUrl(exactVariant.productUrl, exactVariant.id),
      variantId: String(exactVariant.id)
    };
  };

  const captureAttribute = (element, name) => ({
    hadAttribute: element.hasAttribute(name),
    name,
    value: element.getAttribute(name)
  });

  const restoreAttribute = (element, snapshot) => {
    if (snapshot.hadAttribute) {
      element.setAttribute(snapshot.name, snapshot.value ?? '');
    } else {
      element.removeAttribute(snapshot.name);
    }
  };

  const captureContextualSnapshot = (owner, card) => {
    const container = getContextualVariantContainer(card);
    const variants = getContextualVariants(container);
    const canonicalVariantId = String(card.dataset.resolvedVariantId || '');
    const canonicalVariant = variants.find((variant) => String(variant.id) === canonicalVariantId);
    let canonicalOptions = new Map(
      (canonicalVariant?.options || []).map((option) => [
        normalizeOptionValue(option.name),
        normalizeOptionValue(option.value)
      ])
    );
    if (canonicalOptions.size === 0) {
      const renderedVariant = getRenderedFullVariant(card);
      const optionNames = Array.from(container?.querySelectorAll('fieldset') || []).map((fieldset) =>
        fieldset.querySelector('input[data-option-name]')?.dataset.optionName || ''
      );
      if (renderedVariant
        && String(renderedVariant.id) === canonicalVariantId
        && Array.isArray(renderedVariant.options)
        && renderedVariant.options.length === optionNames.length) {
        canonicalOptions = new Map(optionNames.map((name, index) => [
          normalizeOptionValue(name),
          normalizeOptionValue(renderedVariant.options[index])
        ]));
      }
    }
    const inputSnapshots = Array.from(card.querySelectorAll(CONTEXTUAL_OPTION_SELECTOR)).map((input) => {
      const label = input.id
        ? input.closest('fieldset')?.querySelector(`label[for="${CSS.escape(input.id)}"]`)
        : null;
      return {
        attributes: [
          'aria-disabled',
          'class',
          'data-available',
          'data-card-product-id',
          'data-card-variant-has-media',
          'data-card-variant-id',
          'data-product-url'
        ].map((name) => captureAttribute(input, name)),
        disabled: input.disabled,
        input,
        label,
        labelAttributes: label
          ? ['aria-disabled', 'class', 'style', 'tabindex'].map((name) => captureAttribute(label, name))
          : []
      };
    });
    const actionSnapshots = [];
    const actionElements = new Set(card.querySelectorAll('a[href], button, input[name="id"], input[type="submit"], input[type="image"], [role="button"]'));

    actionElements.forEach((action) => {
      const snapshot = {
        action,
        attributes: ['aria-disabled', 'href', 'tabindex'].map((name) => captureAttribute(action, name)),
        disabled: 'disabled' in action ? action.disabled : undefined
      };
      actionSnapshots.push(snapshot);

      if (action.matches('a[href]')) action.removeAttribute('href');
      if ('disabled' in action) action.disabled = true;
      action.setAttribute('aria-disabled', 'true');
      action.setAttribute('tabindex', '-1');
    });

    const snapshot = {
      actionSnapshots,
      canonicalOptions,
      canonicalProductId: String(canonicalVariant?.productId || ''),
      canonicalVariantId,
      cardAriaBusy: captureAttribute(card, 'aria-busy'),
      inputSnapshots,
      ownerAriaBusy: captureAttribute(owner, 'aria-busy')
    };

    owner.setAttribute(CONTEXTUAL_PENDING_ATTRIBUTE, 'true');
    owner.setAttribute('aria-busy', 'true');
    card.setAttribute('aria-busy', 'true');
    return snapshot;
  };

  const restoreContextualSnapshot = (snapshot, card) => {
    if (!snapshot || !card?.isConnected) return;

    snapshot.inputSnapshots.forEach(({ attributes, disabled, input, label, labelAttributes }) => {
      if (!input.isConnected || !card.contains(input)) return;
      attributes.forEach((attribute) => restoreAttribute(input, attribute));
      input.disabled = disabled;
      if (label?.isConnected && card.contains(label)) {
        labelAttributes.forEach((attribute) => restoreAttribute(label, attribute));
      }
    });

    snapshot.inputSnapshots.forEach(({ input }) => {
      if (!input.isConnected || !card.contains(input)) return;
      const canonicalValue = snapshot.canonicalOptions.get(normalizeOptionValue(input.dataset.optionName));
      if (canonicalValue !== undefined) {
        input.checked = canonicalValue === normalizeOptionValue(input.value);
      }
    });

    snapshot.actionSnapshots.forEach(({ action, attributes, disabled }) => {
      if (!action.isConnected || !card.contains(action)) return;
      attributes.forEach((attribute) => restoreAttribute(action, attribute));
      if (disabled !== undefined) action.disabled = disabled;
    });
    restoreAttribute(card, snapshot.cardAriaBusy);
  };

  const releaseContextualQuarantine = (state, restoreActions) => {
    const { owner, snapshot } = state;
    if (restoreActions) restoreContextualSnapshot(snapshot, state.card);
    if (owner?.isConnected) {
      owner.removeAttribute(CONTEXTUAL_PENDING_ATTRIBUTE);
      if (snapshot?.ownerAriaBusy) restoreAttribute(owner, snapshot.ownerAriaBusy);
    }
    state.pending = false;
    state.snapshot = null;
  };

  const getContextualOptionFromTarget = (target) => {
    if (!(target instanceof Element)) return null;
    if (target.matches(CONTEXTUAL_OPTION_SELECTOR)) return target;

    const label = target.closest('label[for]');
    const input = label?.control;
    return input?.matches(CONTEXTUAL_OPTION_SELECTOR) ? input : null;
  };

  const normalizeContextualFilterQuery = (query) => JSON.stringify(
    Array.from(new URLSearchParams(query)).sort(([firstKey, firstValue], [secondKey, secondValue]) =>
      firstKey.localeCompare(secondKey) || firstValue.localeCompare(secondValue)
    )
  );

  const contextualVariantTransactionController = (() => {
    const CONTEXTUAL_ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
    let interactionRevision = 0;
    let keyboardActivation = null;
    let observingContextualTransactions = false;
    let lastLegacyTouchStart = 0;

    const isStateConnected = (state) => Boolean(
      state?.owner?.isConnected
      && state.card?.isConnected
      && state.card.parentElement === state.owner
    );

    const isLatest = (transaction) => {
      const { owner, state } = transaction || {};
      return Boolean(
        transaction
        && activeContextualTransactionStates.has(state)
        && contextualTransactionStates.get(owner) === state
        && state.transaction === transaction
        && state.generation === transaction.generation
        && isStateConnected(state)
      );
    };

    let contextualTransactionObserver;

    const stopObservingContextualTransactions = () => {
      if (activeContextualTransactionStates.size !== 0 || !observingContextualTransactions) return;
      contextualTransactionObserver.disconnect();
      contextualTransactionObserver.takeRecords();
      observingContextualTransactions = false;
    };

    const activateContextualState = (state) => {
      const shouldStartObserving = activeContextualTransactionStates.size === 0;
      activeContextualTransactionStates.add(state);
      if (!shouldStartObserving || !document.documentElement) return;

      contextualTransactionObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
      observingContextualTransactions = true;
    };

    const deactivateContextualState = (state) => {
      activeContextualTransactionStates.delete(state);
      stopObservingContextualTransactions();
    };

    const clearCurrentTransactionReferences = (state, clearOwner = false) => {
      const transaction = state?.transaction;
      if (transaction) {
        transaction.focusState = null;
        if (clearOwner) transaction.owner = null;
      }
      if (state) state.transaction = null;
    };

    const completeLatestTransaction = (transaction) => {
      if (!isLatest(transaction)) return false;
      const { state } = transaction;
      clearCurrentTransactionReferences(state);
      deactivateContextualState(state);
      return true;
    };

    const discardDetachedState = (state) => {
      if (!activeContextualTransactionStates.has(state)) return;

      const owner = state.owner;
      const controller = state.controller;
      state.generation += 1;
      state.controller = null;
      controller?.abort();
      if (owner) contextualTransactionStates.delete(owner);
      clearCurrentTransactionReferences(state, true);
      state.pending = false;
      state.snapshot = null;
      state.card = null;
      state.owner = null;
      deactivateContextualState(state);
    };

    const discardDetachedStates = () => {
      Array.from(activeContextualTransactionStates).forEach((state) => {
        if (!isStateConnected(state)) discardDetachedState(state);
      });
    };

    contextualTransactionObserver = new MutationObserver(discardDetachedStates);

    const restorePendingStateForPagehide = (state) => {
      if (!isStateConnected(state)) return;

      const { card, owner, snapshot } = state;
      if (state.pending) {
        try {
          restoreContextualSnapshot(snapshot, card);
        } catch (error) {
          console.error('Unable to restore the contextual card snapshot during pagehide.', error);
        }

        try {
          card.dataset.resolvedVariantId = snapshot?.canonicalVariantId || card.dataset.resolvedVariantId;
          reconcileRenderedVariantCard(card);
        } catch (error) {
          console.error('Unable to reconcile the contextual card during pagehide.', error);
        }
      }

      try {
        clearContextualLoadingState(card);
      } catch (error) {
        console.error('Unable to clear contextual loading state during pagehide.', error);
      }

      if (state.pending) {
        try {
          releaseContextualQuarantine(state, false);
        } catch (error) {
          console.error('Unable to release the contextual card during pagehide.', error);
          owner.removeAttribute(CONTEXTUAL_PENDING_ATTRIBUTE);
          if (snapshot?.ownerAriaBusy) restoreAttribute(owner, snapshot.ownerAriaBusy);
          if (snapshot?.cardAriaBusy) restoreAttribute(card, snapshot.cardAriaBusy);
          state.pending = false;
          state.snapshot = null;
        }
      } else {
        owner.removeAttribute(CONTEXTUAL_PENDING_ATTRIBUTE);
      }
    };

    const handlePagehide = () => {
      interactionRevision += 1;
      keyboardActivation = null;
      const activeStates = Array.from(activeContextualTransactionStates);

      activeStates.forEach((state) => {
        state.generation += 1;
        if (state.transaction) state.transaction.focusState = null;
      });

      activeStates.forEach((state) => {
        const owner = state.owner;
        const controller = state.controller;
        state.controller = null;
        controller?.abort();
        restorePendingStateForPagehide(state);
        if (owner) contextualTransactionStates.delete(owner);
        clearCurrentTransactionReferences(state, true);
        state.pending = false;
        state.snapshot = null;
        state.card = null;
        state.owner = null;
        deactivateContextualState(state);
      });
    };

    const recover = (transaction, error = null) => {
      if (!isLatest(transaction)) return;
      const { state } = transaction;
      if (error && error.name !== 'AbortError') console.error(error);
      if (!isLatest(transaction)) return;

      const card = state.card;
      try {
        restoreContextualSnapshot(state.snapshot, card);
      } catch (recoveryError) {
        console.error('Unable to restore the contextual card snapshot.', recoveryError);
      }
      if (!isLatest(transaction)) return;

      try {
        card.dataset.resolvedVariantId = state.snapshot?.canonicalVariantId || card.dataset.resolvedVariantId;
        const canonicalVariant = reconcileRenderedVariantCard(card);
        if (canonicalVariant
          && state.snapshot?.canonicalVariantId
          && String(canonicalVariant.id) !== state.snapshot.canonicalVariantId) {
          console.error('Unable to reconcile the contextual card to its canonical variant.');
        }
      } catch (recoveryError) {
        console.error('Unable to reconcile the contextual card to its canonical variant.', recoveryError);
      }
      if (!isLatest(transaction)) return;

      try {
        clearContextualLoadingState(card);
      } catch (recoveryError) {
        console.error('Unable to clear contextual card loading state.', recoveryError);
      }
      if (!isLatest(transaction)) return;
      try {
        releaseContextualQuarantine(state, false);
      } catch (recoveryError) {
        console.error('Unable to release the contextual card action quarantine.', recoveryError);
        if (isLatest(transaction)) {
          const snapshot = state.snapshot;
          state.owner.removeAttribute(CONTEXTUAL_PENDING_ATTRIBUTE);
          if (snapshot?.ownerAriaBusy) restoreAttribute(state.owner, snapshot.ownerAriaBusy);
          if (snapshot?.cardAriaBusy) restoreAttribute(state.card, snapshot.cardAriaBusy);
          state.pending = false;
          state.snapshot = null;
        }
      }
      if (isLatest(transaction)) completeLatestTransaction(transaction);
    };

    const getFragmentElements = (root) => {
      const elements = [root];
      const collect = (scope) => {
        Array.from(scope?.children || []).forEach((element) => {
          elements.push(element);
          if (element.tagName === 'TEMPLATE') collect(element.content);
          collect(element);
        });
      };
      collect(root);
      return elements;
    };

    const hasUniqueFragmentIds = (card) => {
      const ids = new Set();
      return getFragmentElements(card).every((element) => {
        if (!element.hasAttribute('id')) return true;
        const id = element.getAttribute('id');
        if (!id || !id.trim() || /\s/.test(id) || ids.has(id)) return false;
        ids.add(id);
        return true;
      });
    };

    const validateResponse = (html, transaction) => {
      const { request, state } = transaction;
      if (!html?.documentElement) return null;
      const expectedWrapperId = `card-product-clone-${request.sectionId}`;
      const wrappers = getFragmentElements(html.documentElement).filter((element) =>
        element.id === expectedWrapperId
      );
      if (wrappers.length !== 1) return null;

      const wrapper = wrappers[0];
      if (wrapper.tagName !== 'DIV'
        || !wrapper.classList.contains('hidden')
        || !wrapper.classList.contains('product-card-cbl-clone')
        || wrapper.children.length !== 1) {
        return null;
      }

      const card = wrapper.firstElementChild;
      const wrapperElements = getFragmentElements(wrapper);
      const hasOnlySafeDirectNodes = Array.from(wrapper.childNodes).every((node) =>
        node === card || (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === '')
      );
      if (!hasOnlySafeDirectNodes
        || !card?.matches('div.card-product[data-contextual-variant-card="true"]')
        || card.parentElement !== wrapper
        || wrapperElements.filter((element) =>
          element.matches?.('[data-contextual-variant-card="true"]')
        ).length !== 1
        || wrapperElements.some((element) => element !== card && element.classList?.contains('grid-item'))
        || !hasUniqueFragmentIds(card)) {
        return null;
      }

      const cardElements = getFragmentElements(card);
      const fullVariantPayloads = cardElements.filter((element) =>
        element.hasAttribute('data-resolved-current-variant')
      );
      const candidatePayloads = cardElements.filter((element) =>
        element.hasAttribute('data-contextual-variants')
      );
      if (fullVariantPayloads.length !== 1
        || !fullVariantPayloads[0].matches('script[type="application/json"]')
        || candidatePayloads.length !== 1
        || !candidatePayloads[0].matches('script[type="application/vnd.eurus.contextual-variants+json"]')) {
        return null;
      }

      const variant = getRenderedFullVariant(card);
      const container = getContextualVariantContainer(card);
      const candidates = getContextualVariants(container);
      const requiredVariantProperties = [
        'available',
        'compare_at_price',
        'featured_image',
        'id',
        'options',
        'price',
        'title'
      ];
      const hasFullVariant = variant
        && typeof variant === 'object'
        && !Array.isArray(variant)
        && requiredVariantProperties.every((property) =>
          Object.prototype.hasOwnProperty.call(variant, property)
        )
        && Array.isArray(variant.options);
      const candidate = candidates.find((item) =>
        String(item.id) === request.variantId
        && String(item.productId) === request.productId
      );

      if (!isStateConnected(state)
        || !hasFullVariant
        || !container
        || !card.dataset.resolvedVariantId
        || !candidate
        || container.dataset.contextualCollectionHandle !== request.collectionHandle
        || String(container.dataset.contextualProductId || '') !== request.productId
        || container.dataset.contextualSectionId !== request.sectionId
        || String(container.dataset.contextualPage || '') !== request.page
        || normalizeContextualFilterQuery(container.dataset.contextualFilterQuery || '') !== request.filterQuery
        || String(variant.id) !== request.variantId
        || (Object.prototype.hasOwnProperty.call(variant, 'product_id')
          && ((typeof variant.product_id !== 'string' && typeof variant.product_id !== 'number')
            || String(variant.product_id).trim() === ''
            || String(variant.product_id) !== request.productId))
        || String(card.dataset.resolvedVariantId) !== request.variantId
        || Boolean(variant.available) !== Boolean(candidate.available)) {
        return null;
      }

      return { card, variant, wrapper };
    };

    const focusWasStillOwned = (transaction) => {
      const { focusState, state } = transaction;
      if (!focusState
        || interactionRevision !== focusState.interactionRevision
        || !isLatest(transaction)) {
        return false;
      }

      const activeElement = document.activeElement;
      const sourceLabel = focusState.sourceInput.id
        ? state.card.querySelector(`label[for="${CSS.escape(focusState.sourceInput.id)}"]`)
        : null;
      return activeElement === focusState.sourceInput || activeElement === sourceLabel;
    };

    const restoreFocus = (transaction) => {
      if (!isLatest(transaction)) return;
      const { focusState, state } = transaction;
      if (!focusState || interactionRevision !== focusState.interactionRevision) return;
      if (document.activeElement !== document.body && document.activeElement !== document.documentElement) return;

      const input = Array.from(state.card.querySelectorAll(CONTEXTUAL_OPTION_SELECTOR)).find((candidate) =>
        normalizeOptionValue(candidate.dataset.optionName) === focusState.optionName
        && normalizeOptionValue(candidate.value) === focusState.optionValue
      );
      if (!input || input.disabled || input.getAttribute('aria-disabled') === 'true') return;

      const label = input.id
        ? state.card.querySelector(`label[for="${CSS.escape(input.id)}"]`)
        : null;
      const focusTarget = focusState.focusLabel ? label : input;
      if (!focusTarget
        || (focusState.focusLabel
          && (label.getAttribute('aria-disabled') === 'true' || label.tabIndex < 0))) {
        return;
      }
      if (!isLatest(transaction) || interactionRevision !== focusState.interactionRevision) return;

      try {
        focusTarget.focus({ preventScroll: true });
      } catch (_error) {
        focusTarget.focus();
      }
    };

    const dispatchSuccess = (transaction, html, variant, eventName) => {
      requestAnimationFrame(() => {
        if (!isLatest(transaction)) return;
        try {
          document.dispatchEvent(new CustomEvent('eurus:variant:change', {
            detail: { variant }
          }));
          if (!isLatest(transaction)) return;
          if (eventName) {
            document.dispatchEvent(new CustomEvent(eventName, {
              detail: {
                currentVariant: variant,
                html
              }
            }));
          }
        } catch (error) {
          console.error('Unable to dispatch the contextual variant change.', error);
          if (isLatest(transaction)) completeLatestTransaction(transaction);
          return;
        }
        if (!isLatest(transaction)) return;
        requestAnimationFrame(() => {
          if (!isLatest(transaction)) return;
          try {
            restoreFocus(transaction);
          } finally {
            if (isLatest(transaction)) completeLatestTransaction(transaction);
          }
        });
      });
    };

    const requestReplacement = async (transaction) => {
      const { request, state } = transaction;
      const requestOwner = transaction.owner;
      const controller = new AbortController();
      state.controller = controller;

      try {
        const response = await fetch(request.url, { signal: controller.signal });
        if (!isLatest(transaction)) return;
        if (!response.ok) {
          throw new Error(`Contextual product-card request failed with status ${response.status}.`);
        }

        const responseText = await response.text();
        if (!isLatest(transaction)) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (!isLatest(transaction)) return;
        const validated = validateResponse(html, transaction);
        if (!validated) throw new Error('Invalid contextual product-card response.');
        if (!isLatest(transaction)) return;

        const shouldRestoreFocus = focusWasStillOwned(transaction);
        if (!isLatest(transaction)) return;
        state.owner.replaceChildren(validated.card);
        state.card = validated.card;
        if (!isLatest(transaction)) return;

        const eventName = getContextualVariantContainer(state.card)?.dataset.resolvedVariantEvent || '';
        clearContextualLoadingState(state.card);
        if (!isLatest(transaction)) return;
        releaseContextualQuarantine(state, false);
        transaction.focusState = shouldRestoreFocus ? transaction.focusState : null;
        if (!isLatest(transaction)) return;
        dispatchSuccess(transaction, html, validated.variant, eventName);
      } catch (error) {
        recover(transaction, error);
      } finally {
        if (contextualTransactionStates.get(requestOwner) === state
          && state.generation === transaction.generation
          && state.controller === controller) {
          state.controller = null;
        }
      }
    };

    const begin = (input, keyboardOriginated) => {
      const card = input.closest('[data-contextual-variant-card="true"]');
      const owner = card?.closest('.grid-item');
      if (!card?.isConnected || !owner?.isConnected || card.parentElement !== owner) return;

      let state = contextualTransactionStates.get(owner);
      if (!state) {
        state = {
          card,
          controller: null,
          generation: 0,
          owner,
          pending: false,
          snapshot: null,
          transaction: null
        };
        contextualTransactionStates.set(owner, state);
      }

      state.generation += 1;
      state.controller?.abort();
      state.controller = null;
      if (state.transaction) {
        state.transaction.focusState = null;
        state.transaction.owner = null;
      }
      state.card = card;
      state.owner = owner;
      const transaction = {
        focusState: null,
        generation: state.generation,
        owner,
        request: null,
        state
      };
      state.transaction = transaction;
      activateContextualState(state);

      if (!state.pending) {
        state.snapshot = captureContextualSnapshot(owner, card);
        state.pending = true;
      }
      setContextualLoadingState(card, input);

      if (keyboardOriginated) {
        const activeElement = document.activeElement;
        const label = input.id ? card.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
        if (activeElement === input || activeElement === label) {
          transaction.focusState = {
            focusLabel: activeElement === label,
            interactionRevision,
            optionName: normalizeOptionValue(input.dataset.optionName),
            optionValue: normalizeOptionValue(input.value),
            sourceInput: input
          };
        }
      }

      let target;
      try {
        target = resolveContextualVariantTarget(input, card);
      } catch (error) {
        recover(transaction, error);
        return;
      }
      if (!isLatest(transaction)) return;
      if (!target) {
        recover(transaction);
        return;
      }

      const container = getContextualVariantContainer(card);
      const sectionId = container?.dataset.contextualSectionId || '';
      const page = container?.dataset.contextualPage || '';
      const collectionHandle = container?.dataset.contextualCollectionHandle || '';
      const filterQuery = container?.dataset.contextualFilterQuery || '';
      if (!sectionId || !collectionHandle) {
        recover(transaction, new Error('Missing contextual product-card section metadata.'));
        return;
      }

      let requestUrl;
      try {
        requestUrl = new URL(window.Eurus.buildContextualProductSectionUrl(
          target.url,
          sectionId,
          page,
          filterQuery
        ), window.location.origin);
      } catch (error) {
        recover(transaction, error);
        return;
      }
      if (requestUrl.origin !== window.location.origin) {
        recover(transaction, new Error('Refusing a cross-origin contextual product-card request.'));
        return;
      }

      transaction.request = {
        collectionHandle,
        filterQuery: normalizeContextualFilterQuery(filterQuery),
        page: String(page),
        productId: target.productId,
        sectionId,
        url: requestUrl.toString(),
        variantId: target.variantId
      };
      requestReplacement(transaction);
    };

    const getContextualInputDescriptor = (input) => {
      if (!input) return null;
      const card = input.closest('[data-contextual-variant-card="true"]');
      const container = getContextualVariantContainer(card);
      if (!card || !container) return null;

      return {
        groupName: input.name || '',
        inputId: input.id || '',
        optionName: normalizeOptionValue(input.dataset.optionName),
        ownerKey: JSON.stringify([
          container.dataset.contextualSectionId || '',
          container.dataset.contextualPage || ''
        ])
      };
    };

    const activationMatchesGroup = (activation, descriptor) => Boolean(
      activation
      && descriptor
      && activation.ownerKey === descriptor.ownerKey
      && activation.groupName === descriptor.groupName
      && activation.optionName === descriptor.optionName
    );

    const handleChange = (event) => {
      const input = event.target?.matches?.(CONTEXTUAL_OPTION_SELECTOR) ? event.target : null;
      if (!input) return;

      const descriptor = getContextualInputDescriptor(input);
      const keyboardOriginated = Boolean(
        keyboardActivation
        && keyboardActivation.interactionRevision === interactionRevision
        && (
          (keyboardActivation.kind === 'direct'
            && keyboardActivation.ownerKey === descriptor?.ownerKey
            && keyboardActivation.inputId === descriptor?.inputId)
          || (keyboardActivation.kind === 'arrow'
            && input.type === 'radio'
            && input.checked
            && activationMatchesGroup(keyboardActivation, descriptor))
        )
      );
      keyboardActivation = null;
      begin(input, keyboardOriginated);
    };

    const handleKeydown = (event) => {
      interactionRevision += 1;
      const input = getContextualOptionFromTarget(event.target);
      const descriptor = getContextualInputDescriptor(input);
      if (descriptor && (event.key === 'Enter' || event.key === ' ')) {
        keyboardActivation = {
          ...descriptor,
          interactionRevision,
          key: event.key,
          kind: 'direct'
        };
      } else if (descriptor
        && event.target === input
        && input.type === 'radio'
        && CONTEXTUAL_ARROW_KEYS.has(event.key)) {
        keyboardActivation = {
          ...descriptor,
          interactionRevision,
          key: event.key,
          kind: 'arrow'
        };
      } else {
        keyboardActivation = null;
      }
    };

    const handleKeyup = (event) => {
      const activation = keyboardActivation;
      if (!activation || activation.key !== event.key) return;
      setTimeout(() => {
        if (keyboardActivation === activation) keyboardActivation = null;
      }, 0);
    };

    const handlePointerdown = () => {
      interactionRevision += 1;
      keyboardActivation = null;
    };

    const handleLegacyTouchstart = () => {
      lastLegacyTouchStart = Date.now();
      handlePointerdown();
    };

    const handleLegacyMousedown = () => {
      if (Date.now() - lastLegacyTouchStart < 800) return;
      handlePointerdown();
    };

    const handleFocusin = (event) => {
      interactionRevision += 1;
      const input = getContextualOptionFromTarget(event.target);
      const descriptor = getContextualInputDescriptor(input);
      const retainsDirectOwnership = keyboardActivation?.kind === 'direct'
        && keyboardActivation.ownerKey === descriptor?.ownerKey
        && keyboardActivation.inputId === descriptor?.inputId;
      const retainsArrowOwnership = keyboardActivation?.kind === 'arrow'
        && activationMatchesGroup(keyboardActivation, descriptor);
      if (retainsDirectOwnership || retainsArrowOwnership) {
        keyboardActivation.inputId = descriptor.inputId;
        keyboardActivation.interactionRevision = interactionRevision;
        return;
      }
      keyboardActivation = null;
    };

    const handleClick = (event) => {
      const owner = event.target?.closest?.(`.grid-item[${CONTEXTUAL_PENDING_ATTRIBUTE}="true"]`);
      if (!owner || getContextualOptionFromTarget(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    const handleSubmit = (event) => {
      const owner = event.target?.closest?.(`.grid-item[${CONTEXTUAL_PENDING_ATTRIBUTE}="true"]`);
      if (!owner) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    const installProgrammaticSubmitGuard = (methodName) => {
      const nativeMethod = window.HTMLFormElement?.prototype?.[methodName];
      if (typeof nativeMethod !== 'function') return;
      try {
        window.HTMLFormElement.prototype[methodName] = function(...args) {
          if (this.closest?.(`.grid-item[${CONTEXTUAL_PENDING_ATTRIBUTE}="true"]`)) return;
          return Reflect.apply(nativeMethod, this, args);
        };
      } catch (error) {
        console.error(`Unable to install the contextual ${methodName} guard.`, error);
      }
    };

    return {
      install() {
        document.addEventListener('change', handleChange, true);
        document.addEventListener('click', handleClick, true);
        document.addEventListener('auxclick', handleClick, true);
        document.addEventListener('submit', handleSubmit, true);
        document.addEventListener('keydown', handleKeydown, true);
        document.addEventListener('keyup', handleKeyup, true);
        if ('PointerEvent' in window) {
          document.addEventListener('pointerdown', handlePointerdown, true);
        } else {
          document.addEventListener('mousedown', handleLegacyMousedown, true);
          document.addEventListener('touchstart', handleLegacyTouchstart, { capture: true, passive: true });
        }
        document.addEventListener('focusin', handleFocusin, true);
        window.addEventListener('pagehide', handlePagehide);
        installProgrammaticSubmitGuard('submit');
        installProgrammaticSubmitGuard('requestSubmit');
      }
    };
  })();

  window.addEventListener('pageshow', () => {
    requestAnimationFrame(() => {
      document.querySelectorAll('[data-contextual-variant-card="true"]').forEach((card) => {
        reconcileRenderedVariantCard(card);
      });
    });
  });

  window.Eurus.buildContextualProductSectionUrl = (url, sectionId, page = '', filterQuery = '') => {
    const sectionUrl = new URL(url, window.location.origin);
    Array.from(sectionUrl.searchParams.keys()).forEach((key) => {
      if (key.startsWith('filter.')) sectionUrl.searchParams.delete(key);
    });
    new URLSearchParams(filterQuery).forEach((value, key) => {
      if (key.startsWith('filter.')) sectionUrl.searchParams.append(key, value);
    });
    sectionUrl.searchParams.delete('section_id');
    sectionUrl.searchParams.delete('page');
    sectionUrl.searchParams.set('section_id', sectionId);
    if (page !== '') sectionUrl.searchParams.set('page', page);

    return sectionUrl.origin === window.location.origin
      ? `${sectionUrl.pathname}${sectionUrl.search}${sectionUrl.hash}`
      : sectionUrl.toString();
  };

  window.Eurus.initResolvedVariantCard = (card, _eventName) => {
    if (!card || card.dataset.contextualVariantCard !== 'true') return;
    reconcileRenderedVariantCard(card);
  };

  contextualVariantTransactionController.install();

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xVariantSelectCard', (
        element,
        sectionId,
        productId,
        productUrl,
        customizableChooseOption,
        chooseOption,
        productBundle,
        pageParam,
        chooseOptionLabel,
        addToCartLabel,
        blockId,
        handleSectionId
      ) => ({
        loadingEls: '',
        currentVariant: {},
        cachedResults: [],
        clearProductCardHandler: null,
        init() {
          if (element.closest('[data-contextual-variant-card="true"]')) return;
          if (this.clearProductCardHandler) {
            document.removeEventListener(`eurus:product-card:clear:${productId}`, this.clearProductCardHandler);
          }
          this.clearProductCardHandler = () => {
            this._reloadCardProduct();
          };
          document.addEventListener(`eurus:product-card:clear:${productId}`, this.clearProductCardHandler);
        },
        destroy() {
          if (this.clearProductCardHandler) {
            document.removeEventListener(`eurus:product-card:clear:${productId}`, this.clearProductCardHandler);
            this.clearProductCardHandler = null;
          }
        },
        _dispatchUpdateVariant(html="") {
          window.requestAnimationFrame(() => {
            setTimeout(() => { this.loadingEls = ''; }, 100);
          })
          document.dispatchEvent(new CustomEvent(`eurus:product-card-variant-select:updated:${sectionId}:${productUrl}`, {
            detail: {
              currentVariant: this.currentVariant,
              html: html
            }
          }));
        },
        _renderCardProductFormInput(formSrc, formDes) {
          const desInput = formDes.querySelectorAll('input');
          desInput?.forEach((input) => {
            if (input.name !== 'section-id' && input.name) {
              const src = formSrc.querySelector(`input[name=${CSS.escape(input.name)}]`);
              if (src) {
                input.value = src.value;
                input.checked = src.checked;
                input.disabled = src.disabled;
              }
            }
          });
        },
        _renderCardProductFormButton(formSrc, formDes) {
          const srcButton = formSrc.querySelector(`button`);
          if (srcButton) {
            const srcButtonText = srcButton.querySelector('.button-text');
            
            const desButton = formDes.querySelectorAll('button[type="submit"]');
            desButton.forEach((button) => {
              button.disabled = srcButton.disabled;
              button.ariaLabel = srcButton.ariaLabel;

              const desButtonText = button.querySelector('.button-text');
              if (srcButtonText.textContent.trim() == chooseOptionLabel) {
                srcButtonText.textContent = addToCartLabel;
              }
              if (desButtonText && srcButtonText) {
                desButtonText.innerHTML = srcButtonText.innerHTML;
              }
            });
          }
        },
        _renderCardProductForm(html) {
          let target = chooseOption ? '.choose-options' : '.card-product'

          const formSrc = html.querySelector('form[id*="product-form-"]');
          let formDesAll = element.closest(target)?.querySelectorAll('form[id*="product-form-"]');
          if (formSrc && formDesAll) { 
            formDesAll.forEach(formDes => {
              this._renderCardProductFormInput(formSrc, formDes);
              this._renderCardProductFormButton(formSrc, formDes);
            });
          }
        },
        _renderCardProductCurrentVariant(optionSrc, optionDes) {
          const currentVariantSrc = optionSrc.querySelector('.current-variant');
          const currentVariantDes = optionDes.querySelector('.current-variant');

          if (currentVariantSrc && currentVariantDes) {
            currentVariantDes.innerHTML = currentVariantSrc.innerHTML;
          }
        },
        _renderCardProductInputs(optionSrc, optionDes) {
          const inputsSrc = optionSrc.querySelectorAll('input');
          const inputsDes = optionDes.querySelectorAll('input');

          inputsDes?.forEach(des => {
            const src = Array.from(inputsSrc).find((inputSrc) => inputSrc.dataset.optionValueId === des.dataset.optionValueId);
            if (src && des) {
              if (src.classList.contains('disabled')) {
                des.classList.add('disabled');
              } else {
                des.classList.remove('disabled');
              }
              des.checked = src.checked;
              des.dataset.available = src.dataset.available;
            }
          });
        },
        _renderCardProductOptions(optionSrc, optionDes) {
          const optionsSrc = optionSrc.querySelectorAll('option');
          const optionsDes = optionDes.querySelectorAll('option');

          optionsDes?.forEach(des => {
            const src = Array.from(optionsSrc).find((optionSrc) => optionSrc.dataset.optionValueId === des.dataset.optionValueId);
            if (src && des) {
              des.selected = src.selected;
              des.innerHTML = src.innerHTML;
            }
          });
        },
        _renderCardProductInputLabels(optionSrc, optionDes) {
          const labelsSrc = optionSrc.querySelectorAll('label');
          const labelsDes = optionDes.querySelectorAll('label');

          labelsDes?.forEach(des => {
            const src = Array.from(labelsSrc).find((labelSrc) => labelSrc.dataset.optionValueId === des.dataset.optionValueId);
            if (src) {
              if (des.style.getPropertyValue('--bg-image').trim() !== '') {
                des.style.setProperty('--bg-image', src.style.getPropertyValue('--bg-image'));
              }
              if (des.style.getPropertyValue('--ratio-image').trim() !== '') {
                des.style.setProperty('--ratio-image', src.style.getPropertyValue('--ratio-image'));
              }
            }
          });
        },
        _renderCardProductValueVariant(optionSrc, optionDes) {
          const scriptsSrc = optionSrc.querySelectorAll('script[type="application/json"][data-option-value-id]');
          const scriptsDes = optionDes.querySelectorAll('script[type="application/json"][data-option-value-id]');

          scriptsDes?.forEach(des => {
            const src = Array.from(scriptsSrc).find((scriptSrc) => scriptSrc.dataset.optionValueId === des.dataset.optionValueId);
            if (src && des) {
              des.textContent = src.textContent;
            }
          })
        },
        _renderCardProductOption(html) {
          const optionSrc = html.querySelector('.x-variants-data-js');
          const optionDes = element.closest('.x-variants-data-js');

          if (optionSrc && optionDes) {
            this._renderCardProductCurrentVariant(optionSrc, optionDes);
            this._renderCardProductInputs(optionSrc, optionDes);
            this._renderCardProductOptions(optionSrc, optionDes);
            this._renderCardProductInputLabels(optionSrc, optionDes);
            this._renderCardProductValueVariant(optionSrc, optionDes);
          }
        },
        _renderCardProductPrice(html) {
          let target = chooseOption ? '.choose-options' : '.card-product'

          const priceSrc = html.querySelector((chooseOption || productBundle) ? '.x-card-price .choose-option-price' : '.x-card-price .product-card-price');
          const priceDes = element.closest(target).querySelector('.x-card-price');
          if (this.currentVariant == null) {
            priceDes.classList.add('hidden');
            return;
          } else {
            priceDes.classList.remove('hidden');
          }

          const priceRange = priceDes.querySelector('.price-range');
          if (priceSrc && priceDes) {
            priceDes.innerHTML = priceSrc.innerHTML;
            if (priceRange) {
              const priceRangeDes = priceDes.querySelector('.price-range')
              if (priceRangeDes) {
                priceRangeDes.innerHTML = priceRange.innerHTML;
                return;
              }
              const priceEl = priceDes.querySelector('.price');
              if (priceEl) {
                priceEl.innerHTML += priceRange.outerHTML;
              }
            }
          }
        },
        _renderCardProductLabels(html) {
          const labelDataSrc = html.querySelector('.x-labels-data');
          const labelDataDes = element.closest('.card-product').querySelector('.x-labels-data');

          if (labelDataSrc && labelDataDes) {
            labelDataDes.setAttribute('x-labels-data', labelDataSrc.getAttribute('x-labels-data'));
            this.$nextTick(() => {
              Alpine.store('xBadges') && Alpine.store('xBadges').load(labelDataDes, () => {}, null, true);
            });
          }
        },
        _renderCardProductAvailabilityNotice(html) {
          const noticeSrc = html.getElementById('x-availability-notice');
          const noticeDes = element.closest('.card-product').querySelector('div[id*="x-availability-notice"]');

          if (noticeSrc && noticeDes) {
            noticeDes.innerHTML = noticeSrc.innerHTML;
          }
        },
        _renderCardProductImage(html) {
          const variantImageSrc = html.getElementById('x-variant-image').dataset.imageUrl;
          const cardProduct = element.closest('.card-product');
          if (variantImageSrc != '') {
            let media = cardProduct.querySelector(`[media="${variantImageSrc}"]`);
            if (media) {
              let index = media.getAttribute('index');
              let slide = cardProduct.querySelector('.x-splide');
              if (slide) {
                if (slide.splide) {
                  slide.splide.go(Number(index));
                } else {
                  document.addEventListener(`eurus:${sectionId}:splide-ready`, () => {
                    slide.splide.go(Number(index));
                  });
                }
                return;
              } 
            }
            const previewImg = cardProduct.getElementsByClassName("preview-img")[0];
            if (!previewImg) return;
            previewImg.removeAttribute("srcset");
            previewImg.setAttribute("src", variantImageSrc);
            let slide = cardProduct.querySelector('.x-splide');
            if (slide && slide.splide) {
              slide.splide.go(0);
            }
          } else {
            let slide = cardProduct.querySelector('.x-splide');
            if (slide && slide.splide) {
              slide.splide.go(0);
            }
          }
        },
        _renderLinkedProductInput() {
          const container = element.closest('.x-product-data-container');
          let desInput =
            container?.querySelector('input[name="properties[_linked_product_id][]"]') ||
            container?.querySelector('input[name="properties[_linked_product_id_qty][]"]');
          
          if (desInput) {
            if (this.currentVariant?.available) {
              desInput.value = this.currentVariant.id;
              desInput.disabled = false;
            } else {
              desInput.value = "";
              desInput.disabled = true;
            }
            desInput.classList.remove('customization-picker','validate-checkbox', 'required-picker');
          }
        },
        _renderCurrentOptionText(html) {
          const desSelectedValueText = element.closest('.x-product-data-container')?.querySelectorAll('.option-selected-value');
          const srcSelectedValueText = html.querySelectorAll('.option-selected-value');

          desSelectedValueText?.forEach(des => {
            const src = Array.from(srcSelectedValueText).find((srcText) => srcText.dataset.optionName == des.dataset.optionName);
            if (src && des) {
              des.classList.remove('hidden');
              des.innerHTML = src.innerHTML;
            }
          });
        },
        _renderCustomizableOptionSelectedVariant() {
          const desSelectedVariantText = element.closest('fieldset:has(.x-product-data-container)').querySelector('.selected-variant-name');
          if (desSelectedVariantText) {
            desSelectedVariantText.textContent = `: ${this.currentVariant.name}`;
          }
        },
        _renderCustomizableOptionPriceText(html) {
          const desVariantPriceText = element.closest('fieldset:has(.x-product-data-container)').querySelector(`.variant-price-${productId}`);
          const srcVariantPriceText = html.querySelector(`.variant-price-${productId}`);
          if (desVariantPriceText && srcVariantPriceText) {
            desVariantPriceText.innerHTML = `+ ${srcVariantPriceText.innerHTML}`;
          }
        },
        _getCurrentVariant(html) {
          this.currentVariant = JSON.parse(html.querySelector('.x-variants-data-js script[type="application/json"]')?.textContent);
        },
        _getCurrentVariantCbl(html) {
          this.currentVariant = JSON.parse(html.querySelector(`${chooseOption ? '#choose-options-content' : '.product-card-cbl-clone'} script[type="application/json"]`)?.textContent)
        },
        _checkOptionAndCache(optionNum, targetUrl) {
          const selectedOptionValues = this._getSelectedOptionValues();

          if (optionNum !== -1) {
            if (selectedOptionValues.length < Number(optionNum)) {
              return false;
            };
          }

          if (selectedOptionValues.length === 0) return false;

          let params = `option_values=${selectedOptionValues.join(',')}`;
          if (chooseOption || productBundle) {
            params = `option_values=${selectedOptionValues.join(',')}&page=${pageParam}`;
          }

          const link = `${targetUrl.split("?")[0]}?section_id=card-product&${params}`;

          if (this.cachedResults[link] && this.cachedResults[link] !== '') {
            return false;
          }

          return true;
        },
        updateProductCard(optionNum, target, isByob, isProductList = false) {
          let callback = () => {};

          const isCbl = target && target !== element.dataset.url;
          const targetUrl = isCbl ? target : element.dataset.url;

          const canLoad = this._checkOptionAndCache(optionNum, targetUrl);

          if (isCbl) {
            callback = (html) => {
              window.requestAnimationFrame(() => {
                this._getCurrentVariantCbl(html);
                this._replaceProductCardCbl(html);
                requestAnimationFrame(() => {
                  this._dispatchUpdateVariant(html);
                });
                document.dispatchEvent(new CustomEvent("eurus:variant:change", {
                  detail: {
                    variant: this.currentVariant
                  }
                }));
              });
            }
            this._renderProductCardCbl(targetUrl, callback);
            return canLoad;
          }

          if (chooseOption) {
            callback = (html) => {
              window.requestAnimationFrame(() => {
                this._getCurrentVariant(html);
                this._renderCardProductForm(html);
                this._renderCurrentOptionText(html);
                this._renderCardProductOption(html);
                this._renderCardProductPrice(html);
                this._dispatchUpdateVariant(html);
                document.dispatchEvent(new CustomEvent("eurus:variant:change", {
                  detail: {
                    variant: this.currentVariant
                  }
                }));
              });
            }
          } else if (customizableChooseOption) {
            callback = (html) => {
              window.requestAnimationFrame(() => {
                this._getCurrentVariant(html);
                this._renderCardProductOption(html);
                this._renderLinkedProductInput();
                this._renderCurrentOptionText(html);
                this._renderCustomizableOptionSelectedVariant();
                this._renderCustomizableOptionPriceText(html);
                this._dispatchUpdateVariant(html);
                document.dispatchEvent(new CustomEvent("eurus:variant:change", {
                  detail: {
                    variant: this.currentVariant
                  }
                }));
              });
            }
          } else {
            callback = (html) => {
              window.requestAnimationFrame(() => {
                this._getCurrentVariant(html);
                this._renderCardProductForm(html);
                this._renderCardProductOption(html);
                this._renderCardProductPrice(html);
                this._renderCardProductLabels(html);
                this._renderCurrentOptionText(html);
                this._renderCardProductAvailabilityNotice(html);
                this._renderCardProductImage(html);
                this._dispatchUpdateVariant(html);
                document.dispatchEvent(new CustomEvent("eurus:variant:change", {
                  detail: {
                    variant: this.currentVariant
                  }
                }));
                
                if (isByob || isProductList) {
                  document.dispatchEvent(new CustomEvent('eurus:product-card-update', {
                    detail: {
                      sectionId: handleSectionId
                    }
                  }));
                }
              });
            }
          }

          this._renderProductCard(targetUrl, optionNum, callback);
          return canLoad;
        },
        _reloadCardProduct() {
          let link = "";
          const selectedOptionValues = this._getSelectedOptionValues()
          let params = `option_values=${selectedOptionValues.join(',')}`;
          if (chooseOption || productBundle) {
            params = `option_values=${selectedOptionValues.join(',')}&page=${pageParam}`;
          }
          link = `${productUrl.split("?")[0]}?section_id=card-product&${params}`;
          if (this.cachedResults[link] && this.cachedResults[link] !== '') {
            this.cachedResults[link] = '';
          }

          if (chooseOption) {
            callback = (html) => {
              window.requestAnimationFrame(() => {
                this._getCurrentVariant(html);
                this._renderCardProductForm(html);
                this._renderCurrentOptionText(html);
                this._renderCardProductOption(html);
                this._renderCardProductPrice(html);
                this._dispatchUpdateVariant(html);
              });
            }
          } else if (customizableChooseOption) {
            callback = (html) => {
              window.requestAnimationFrame(() => {
                this._getCurrentVariant(html);
                this._renderCardProductOption(html);
                this._renderLinkedProductInput();
                this._renderCurrentOptionText(html);
                this._renderCustomizableOptionSelectedVariant();
                this._renderCustomizableOptionPriceText(html);
                this._dispatchUpdateVariant(html);
              });
            }
          } else {
            callback = (html) => {
              window.requestAnimationFrame(() => {
                this._getCurrentVariant(html);
                this._renderCardProductForm(html);
                this._renderCardProductOption(html);
                this._renderCardProductPrice(html);
                this._renderCardProductLabels(html);
                this._renderCardProductAvailabilityNotice(html);
                this._renderCardProductImage(html);
                this._dispatchUpdateVariant(html);
              });
            }
          }

          this._renderProductCard(productUrl, -1, callback);
        },
        _renderProductCard(url, optionNum, callback) {
          let link = "";
          const selectedOptionValues = this._getSelectedOptionValues();
          if (optionNum !== -1) {
            if (selectedOptionValues.length < Number(optionNum)) {
              window.requestAnimationFrame(() => {
                setTimeout(() => { this.loadingEls = ''; }, 100);
              });

              return
            };
          }
          if (selectedOptionValues.length === 0) return;
          let params = `option_values=${selectedOptionValues.join(',')}`;
          if (chooseOption || productBundle) {
            params = `option_values=${selectedOptionValues.join(',')}&page=${pageParam}`;
          }
          link = `${url.split("?")[0]}?section_id=card-product&${params}`;
          Alpine.store('xHelper').cancelRequest(`eurus:product_card_request:${productId}`);
          if (this.cachedResults[link] && this.cachedResults[link] !== '') {
            const html = this.cachedResults[link];
            window.requestAnimationFrame(() => {
              callback(html);
            });
          } else {
            const controller = new AbortController();
            Alpine.store('xHelper').requestControllers.set(`eurus:product_card_request:${productId}`, controller);
            fetch(link, { signal: controller.signal })
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                window.requestAnimationFrame(() => {
                  callback(html);
                });
                this.cachedResults[link] = html;
              }).catch(err => {
                if (err.name !== 'AbortError') {
                  console.error(err);
                }
                window.requestAnimationFrame(() => {
                  setTimeout(() => { this.loadingEls = ''; }, 100);
                })
              })
          }
        },
        _renderProductCardCbl(url, callback) {
          let link = `${url}?section_id=${handleSectionId}&page=${pageParam}`;

          Alpine.store('xHelper').cancelRequest(`eurus:product_card_request:${productId}`);
          if (this.cachedResults[link] && this.cachedResults[link] !== '') {
            const html = this.cachedResults[link];
            window.requestAnimationFrame(() => {
              callback(html);
            });
          } else {
            const controller = new AbortController();
            Alpine.store('xHelper').requestControllers.set(`eurus:product_card_request:${productId}`, controller);
            fetch(link, { signal: controller.signal })
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                window.requestAnimationFrame(() => {
                  callback(html);
                });
                this.cachedResults[link] = html;
              }).catch(err => {
                if (err.name !== 'AbortError') {
                  console.error(err);
                }
              })
          }
        },
        _replaceProductCardCbl(html) {
          let src, des;
          if (chooseOption) {
            src = html.querySelector('#choose-options-content .choose-options');
            des = element.closest(`.choose-options`);
          } else {
            src = html.getElementById(`card-product-clone-${handleSectionId}${blockId ? `-${blockId}` : ''}`);
            des = element.closest('.card-product');
          }

          if (src && des) {
            des.innerHTML = src.innerHTML;
          }
        },
        _getSelectedOptionValues() {
          return Array.from(element.querySelectorAll('select option[selected][value], fieldset input:checked')).map(
            (e) => e.dataset.optionValueId
          );
        }
      }))
    });
  });
}
